import { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";

import {
  PennButton,
  PennCheckbox,
  PennInput,
  PennModal,
  PennText,
} from "@/components/ui";
import { useAllMyApplications, useDeleteApplication } from "@/hooks/useApplications";
import { useWorkFor } from "@/hooks/useWorkFor";
import type { ApplicationSummary } from "@/api/applications";
import { useAppSelector } from "@/stores/hooks";

export type Application = ApplicationSummary;

type ExistingApplicationsModalProps = {
  open: boolean;
  onClose: () => void;
  /** Pre-check rows already on the filter page (POC keeps prior picks when adding more). */
  initialSelectedIds?: string[];
  onSelect?: (apps: Application[]) => void;
  onNew?: () => void;
  onEdit?: (app: Application) => void;
};

function describeError(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? "Failed to load applications.";
  }
  return "Failed to load applications.";
}

export const ExistingApplicationsModal = ({
  open,
  onClose,
  initialSelectedIds = [],
  onSelect,
  onNew,
  onEdit,
}: ExistingApplicationsModalProps) => {
  const [search, setSearch] = useState("");
  const [colFilters, setColFilters] = useState({
    name: "",
    appNo: "",
    oem: "",
    program: "",
    company: "",
    owner: "",
  });
  const [selected, setSelected] = useState<string[]>([]);
  const currentUser = useAppSelector((state) => state.auth.user);
  const workFor = useWorkFor();

  useEffect(() => {
    if (!open) return;
    setSelected(initialSelectedIds);
    // C8 / POC parity: every search + column
    // filter is reset each time the modal is opened.
    setSearch("");
    setColFilters({
      name: "",
      appNo: "",
      oem: "",
      program: "",
      company: "",
      owner: "",
    });
  }, [open, initialSelectedIds]);

  // Issue #8: fetch EVERY saved application (no page cap) — pages through the
  // endpoint until exhausted so the modal shows the complete list.
  const { data, isLoading, isError, error, refetch, isFetching } =
    useAllMyApplications({ enabled: open });
  const del = useDeleteApplication();

  // Applications have no company column of their own — the owning company is
  // joined from the owner's user profile (`ownerCompanyName`) so Work-For apps
  // (owned by a different customer) show that customer's company instead of a
  // blank. Falls back to the signed-in user's company for legacy rows where the
  // API hasn't resolved an owner company yet.
  const resolveCompany = useCallback(
    (app: ApplicationSummary): string => {
      const fromApi = app.ownerCompanyName?.trim();
      if (fromApi) return fromApi;
      const ownerEmail = (app.createdByEmail ?? "").trim().toLowerCase();
      const myEmail = (currentUser?.email ?? "").trim().toLowerCase();
      if (!ownerEmail || ownerEmail === myEmail) {
        return currentUser?.companyName?.trim() ?? "";
      }
      return "";
    },
    [currentUser],
  );

  const items = useMemo(() => data?.items ?? [], [data]);

  // Identity scoping. When WORKING FOR someone, `/v1/applications/mine` is
  // already scoped server-side to the active target (svc-06 honours the
  // x-penn-work-for-* headers on this route, and useAllMyApplications keys the
  // cache by identity) — pass through; re-matching by email here would drop
  // rows whose owner email was stamped before an address change. Acting as
  // SELF the endpoint still returns the historical UNION of rows the caller
  // owns OR created as a Work-For impersonator, so narrow to only their own.
  const ownedByActiveIdentity = useMemo(() => {
    if (workFor.kind !== "self") return items;
    const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();
    const self = norm(currentUser?.email);
    return items.filter((a) => norm(a.createdByEmail) === self);
  }, [items, workFor, currentUser?.email]);

  const filtered = useMemo(() => {
    const norm = (v: string | null | undefined) => (v ?? "").toLowerCase();
    const matches = (value: string, needle: string) =>
      norm(value).includes(needle.trim().toLowerCase());
    const q = search.trim().toLowerCase();
    const cf = colFilters;
    return ownedByActiveIdentity.filter((a) => {
      const company = resolveCompany(a);
      // Global search spans every visible column (POC).
      const haystack = [
        a.name,
        a.applicationNumber,
        a.oem,
        a.productionProgram,
        company,
        a.workingForEmail ?? a.createdByEmail,
      ]
        .map(norm)
        .join(" ");
      if (q && !haystack.includes(q)) return false;
      // C1 / POC parity: each column filter scopes
      // to its own field instead of the shared haystack the MVP used before.
      if (cf.name && !matches(a.name ?? "", cf.name)) return false;
      if (cf.appNo && !matches(a.applicationNumber ?? "", cf.appNo)) return false;
      if (cf.oem && !matches(a.oem ?? "", cf.oem)) return false;
      if (cf.program && !matches(a.productionProgram ?? "", cf.program))
        return false;
      if (cf.company && !matches(company, cf.company)) return false;
      if (cf.owner && !matches(a.workingForEmail ?? a.createdByEmail ?? "", cf.owner))
        return false;
      return true;
    });
  }, [ownedByActiveIdentity, search, colFilters, resolveCompany]);

  const toggleSelect = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const selectAll = () => setSelected(filtered.map((a) => a.applicationId));

  const handleConfirm = () => {
    const byId = new Map(items.map((a) => [a.applicationId, a]));
    const apps = selected
      .map((id) => byId.get(id))
      .filter((a): a is Application => a != null);
    onSelect?.(apps);
    onClose();
  };

  const handleDelete = async (id: string) => {
    await del.mutateAsync(id).catch(() => undefined);
    setSelected((prev) => prev.filter((x) => x !== id));
  };

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <PennButton type="button" variant="secondary" size="md" onClick={onClose}>
        Close
      </PennButton>
      <PennButton
        type="button"
        variant="primary"
        size="md"
        onClick={handleConfirm}
        disabled={selected.length === 0}
      >
        OK
      </PennButton>
    </div>
  );

  return (
    <PennModal
      open={open}
      onClose={onClose}
      // label="My applications"
      title="My applications"
      maxWidth="max-w-7xl"
      footer={footer}
    >
      <div className="p-6">
        <div className="flex flex-wrap items-center gap-3">
          <PennInput
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or application number"
            className="flex-1 min-w-[220px]"
          />
          <PennButton type="button" variant="secondary" size="md" onClick={selectAll}>
            Select all
          </PennButton>
          <PennButton type="button" variant="primary" size="md" onClick={onNew}>
            New application
          </PennButton>
          <PennButton
            type="button"
            variant="secondary"
            size="md"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </PennButton>
        </div>

        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {(
            [
              { key: "name", placeholder: "Filter name" },
              { key: "appNo", placeholder: "Filter panel part no." },
              { key: "oem", placeholder: "Filter OEM" },
              { key: "program", placeholder: "Filter program" },
              { key: "company", placeholder: "Filter company" },
              { key: "owner", placeholder: "Filter owner" },
            ] as const
          ).map(({ key, placeholder }) => (
            <PennInput
              key={key}
              type="text"
              value={colFilters[key]}
              onChange={(e) => setColFilters((p) => ({ ...p, [key]: e.target.value }))}
              placeholder={placeholder}
            />
          ))}
        </div>

        {isLoading ? (
          <div className="mt-6 py-8 text-center border border-dashed border-gray-200 rounded-2xl">
            <PennText as="p" size="sm" color="slate-500">
              Loading applications…
            </PennText>
          </div>
        ) : isError ? (
          <div className="mt-6 py-8 text-center border border-dashed border-red-200 rounded-2xl">
            <PennText as="p" size="sm" color="error">
              {describeError(error)}
            </PennText>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-6 py-8 text-center border border-dashed border-gray-200 rounded-2xl px-3">
            <PennText as="p" size="sm" color="slate-500">
              {workFor.kind === "self"
                ? "No saved applications found for your account."
                : `No saved applications found for ${workFor.email}${workFor.kind === "registered" && workFor.name ? ` (${workFor.name})` : ""}.`}
            </PennText>
          </div>
        ) : (
          <div className="mt-4 border border-gray-200 rounded-2xl max-h-[50vh] overflow-auto penn-scrollbar">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 sticky top-0">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Select</th>
                  <th className="text-left font-semibold px-4 py-3">Name</th>
                  <th className="text-left font-semibold px-4 py-3">Panel Part No.</th>
                  <th className="text-left font-semibold px-4 py-3">OEM</th>
                  <th className="text-left font-semibold px-4 py-3">Program</th>
                  <th className="text-left font-semibold px-4 py-3">Company</th>
                  <th className="text-left font-semibold px-4 py-3">Owner</th>
                  <th className="text-right font-semibold px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filtered.map((app) => (
                  <tr
                    key={app.applicationId}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleSelect(app.applicationId)}
                  >
                    {/* C8 / POC parity: clicking
                        anywhere on the row toggles its checkbox. The checkbox and
                        action cells stop propagation so they fire exactly once. */}
                    <td
                      className="px-4 py-3 align-top"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <PennCheckbox
                        checked={selected.includes(app.applicationId)}
                        onChange={() => toggleSelect(app.applicationId)}
                      />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-gray-900">{app.name ?? "—"}</div>
                      <div className="text-xs text-gray-500">{app.applicationId}</div>
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap text-gray-800">
                      {app.applicationNumber ?? "—"}
                    </td>
                    <td className="px-4 py-3 align-top text-gray-800">{app.oem ?? "—"}</td>
                    <td className="px-4 py-3 align-top text-gray-800">
                      {app.productionProgram ?? "—"}
                    </td>
                    <td className="px-4 py-3 align-top text-gray-800">
                      {resolveCompany(app) || "—"}
                    </td>
                    <td className="px-4 py-3 align-top text-gray-800">
                      {app.workingForEmail ?? app.createdByEmail ?? "—"}
                    </td>
                    <td
                      className="px-4 py-3 align-top text-right whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-2">
                        {onEdit && (
                          <PennButton
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => onEdit(app)}
                          >
                            Edit
                          </PennButton>
                        )}
                        <PennButton
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDelete(app.applicationId)}
                          disabled={del.isPending}
                        >
                          Delete
                        </PennButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PennModal>
  );
};
